import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, message, Form, Input, Select, Radio, Divider, Tag, Progress, Space, Tooltip, Modal } from 'antd';
import {
  InboxOutlined,
  FileMarkdownOutlined,
  CheckCircleOutlined,
  FolderOpenOutlined,
  FileZipOutlined,
  DeleteOutlined,
  CloudUploadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { skillsApi } from '../services/api';
import axios from 'axios';
import styled from 'styled-components';

const { TextArea } = Input;

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const PublishContainer = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 32px 20px 60px;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 {
    font-size: 26px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  p {
    color: #8c8c8c;
    margin: 0;
  }
`;

const DropZone = styled.div`
  border: 2px dashed ${p => (p.$active ? '#1677ff' : '#d9d9d9')};
  border-radius: 12px;
  padding: 48px 24px;
  background: ${p => (p.$active ? '#e6f4ff' : '#fafafa')};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: #1677ff;
    background: #f0f7ff;
  }
`;

const ChoiceRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const FileList = styled.div`
  margin-top: 16px;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fff;
`;

const FileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13px;
  &:last-child {
    border-bottom: none;
  }
  .name { color: #262626; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .size { color: #8c8c8c; margin-left: 12px; flex-shrink: 0; }
`;

const IconUploader = styled.div`
  width: 120px;
  height: 120px;
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fafafa;
  transition: all 0.2s;
  &:hover {
    border-color: #1677ff;
    background: #f0f7ff;
  }
`;

const categories = [
  { value: 'music', label: '🎵 音乐创作' },
  { value: 'video', label: '🎬 视频制作' },
  { value: 'audio', label: '🎧 音频处理' },
  { value: 'marketing', label: '📢 营销推广' },
  { value: 'data', label: '📊 数据分析' },
  { value: 'distribution', label: '📀 发行管理' },
  { value: 'office', label: '📋 办公效率' },
  { value: 'ai', label: '🤖 AI工具' },
  { value: 'image', label: '🖼️ 图片处理' },
  { value: 'test', label: '🧪 测试开发' },
];

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(2) + ' MB';
}

function Publish() {
  const [form] = Form.useForm();
  const [files, setFiles] = useState([]);          // 已选择的文件列表（文件夹展开后所有文件）
  const [folderMode, setFolderMode] = useState(false); // true = 选文件夹，false = 选 zip
  const [iconFile, setIconFile] = useState(null);
  const [iconUrl, setIconUrl] = useState(null);
  const [packageUrl, setPackageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [localUser, setLocalUser] = useState(null);
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const iconInputRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (saved && token) {
      try { setLocalUser(JSON.parse(saved)); } catch (e) { setLocalUser(null); }
    }
  }, [user, isAuthenticated]);

  const effectiveUser = user || localUser;

  if (!loading && !effectiveUser) {
    return (
      <PublishContainer>
        <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2>需要登录才能访问</h2>
          <p style={{ color: '#666', margin: '20px 0' }}>请先登录后再发布技能</p>
          <Button type="primary" size="large" onClick={() => navigate('/login')}>
            登录 / 注册
          </Button>
        </Card>
      </PublishContainer>
    );
  }

  // ========== 文件选择处理 ==========
  // 选择 zip：单文件
  const handleZipChoose = () => {
    setFolderMode(false);
    fileInputRef.current?.click();
  };

  // 选择文件夹：多文件（带 webkitdirectory）
  const handleFolderChoose = () => {
    setFolderMode(true);
    folderInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    // 重置上次的上传 url
    setPackageUrl(null);
    setFiles(list);
    // 尝试找 SKILL.md 解析预填
    await tryParseSkillMd(list);
    // 清空 input value 以便下次能选同一目录
    e.target.value = '';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    const items = e.dataTransfer.items;
    if (items && items.length > 0 && items[0].webkitGetAsEntry) {
      const all = [];
      const traverse = (entry, path = '') => new Promise((resolve) => {
        if (entry.isFile) {
          entry.file((f) => {
            // 给文件挂上相对路径，便于后端识别结构
            try { Object.defineProperty(f, 'webkitRelativePath', { value: path + entry.name }); } catch (e) {}
            all.push(f);
            resolve();
          });
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          reader.readEntries(async (entries) => {
            for (const en of entries) await traverse(en, path + entry.name + '/');
            resolve();
          });
        } else resolve();
      });
      const entries = [];
      for (let i = 0; i < items.length; i++) {
        const en = items[i].webkitGetAsEntry();
        if (en) entries.push(en);
      }
      await Promise.all(entries.map((en) => traverse(en)));
      if (all.length === 0) return;
      setFolderMode(all.length > 1 || !!entries.find(e => e && e.isDirectory));
      setPackageUrl(null);
      setFiles(all);
      await tryParseSkillMd(all);
    } else {
      // 退化：直接读取 files
      const list = Array.from(e.dataTransfer.files || []);
      if (list.length === 0) return;
      setFolderMode(list.length > 1);
      setPackageUrl(null);
      setFiles(list);
      await tryParseSkillMd(list);
    }
  };

  // 尝试解析 SKILL.md 元数据
  const tryParseSkillMd = async (fileList) => {
    const skillMd = fileList.find((f) =>
      (f.webkitRelativePath || f.name).toLowerCase().endsWith('skill.md') ||
      (f.name || '').toLowerCase() === 'skill.md'
    );
    if (!skillMd) return;
    try {
      const fd = new FormData();
      fd.append('file', skillMd);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/upload/parse-skill-md`, fd, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const meta = res.data?.data?.metadata || {};
      const content = res.data?.data?.content || '';
      const patch = {};
      
      // 使用 AI 润色后的名称（如果有）
      if (meta.namePolished) {
        patch.name = meta.namePolished;
        patch.slug = meta.namePolished.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      } else if (meta.name) {
        patch.name = meta.name;
        patch.slug = meta.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      }
      
      if (meta.version) patch.version = meta.version;
      
      // 直接使用 SKILL.md 内容作为描述
      if (content) {
        patch.description = content;
      } else if (meta.description) {
        patch.description = meta.description;
      }
      
      if (meta.category) {
        // 容错匹配
        const hit = categories.find(c => c.value === meta.category || c.label.includes(meta.category));
        if (hit) patch.category = hit.value;
      }
      if (meta.tags && meta.tags.length) patch.tags = meta.tags.join(', ');
      
      form.setFieldsValue(patch);
      message.success('已根据 SKILL.md 自动填充表单（AI润色名称）');
    } catch (err) {
      console.warn('parse-skill-md failed:', err);
    }
  };

  const removeFile = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
    if (next.length === 0) setPackageUrl(null);
  };

  // ========== 上传 ==========
  const uploadPackage = async () => {
    if (files.length === 0) {
      message.warning('请先选择文件夹或 zip');
      return null;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: token ? `Bearer ${token}` : '' };
      let url;
      if (folderMode || files.length > 1) {
        // 批量上传
        const fd = new FormData();
        files.forEach((f) => {
          // 保留相对路径作为文件名
          const name = f.webkitRelativePath || f.name;
          fd.append('files', f, name);
        });
        const res = await axios.post(`${API_BASE}/upload/batch`, fd, {
          headers,
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
          },
        });
        const list = res.data?.data?.files || [];
        // 找出 SKILL.md 所在文件的入口；没有则用第一个
        const entry = list.find(f => /skill\.md$/i.test(f.fileName)) || list[0];
        url = entry?.url;
        message.success(`✅ 已上传 ${list.length} 个文件`);
      } else {
        const fd = new FormData();
        fd.append('file', files[0]);
        const res = await axios.post(`${API_BASE}/upload/skill`, fd, {
          headers,
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
          },
        });
        url = res.data?.data?.url;
        message.success('✅ 技能包上传成功');
      }
      setPackageUrl(url);
      return url;
    } catch (err) {
      message.error('上传失败：' + (err.response?.data?.message || err.message));
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleIconSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIconFile(file);
    e.target.value = '';
    try {
      const fd = new FormData();
      fd.append('icon', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/upload/icon`, fd, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      setIconUrl(res.data?.data?.url);
    } catch (err) {
      message.error('图标上传失败: ' + (err.response?.data?.message || err.message));
      setIconFile(null);
    }
  };

  const handleSubmit = async (values) => {
    // 必须先有 package_url
    let pkg = packageUrl;
    if (!pkg) {
      pkg = await uploadPackage();
      if (!pkg) return;
    }
    setSubmitting(true);
    try {
      await skillsApi.createSkill({
        name: values.slug,
        display_name: values.name,
        category: values.category,
        description: values.description,
        version: values.version || '1.0.0',
        tags: values.tags ? values.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        package_url: pkg,
        icon_url: iconUrl,
      });
      message.success('技能发布成功！审核通过后将展示在百音技能商店');
      setSubmitted(true);
    } catch (error) {
      message.error(error.response?.data?.message || '发布失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PublishContainer>
        <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
          <CheckCircleOutlined style={{ fontSize: 72, color: '#52c41a' }} />
          <h2 style={{ marginTop: 24 }}>发布成功！</h2>
          <p style={{ color: '#666', marginTop: 16 }}>
            您的技能已提交审核，审核通过后将同步展示在百音技能商店
          </p>
          <Space style={{ marginTop: 24 }}>
            <Button type="primary" size="large" onClick={() => navigate('/skills')}>
              查看百音技能商店
            </Button>
            <Button size="large" onClick={() => window.location.reload()}>
              继续发布
            </Button>
          </Space>
        </Card>
      </PublishContainer>
    );
  }

  return (
    <PublishContainer>
      <PageHeader>
        <h1>发布新技能</h1>
        <p>上传你的 Skill 包（包含 SKILL.md 的文件夹或 zip），审核通过后即可在百音技能商店展示</p>
      </PageHeader>

      <Card>
        {/* ============ 上传区域 ============ */}
        <DropZone
          $active={dragActive}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={handleZipChoose}
        >
          <CloudUploadOutlined style={{ fontSize: 56, color: '#1677ff', marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>
            点击上传或拖入此处
          </div>
          <div style={{ color: '#8c8c8c', fontSize: 13 }}>
            支持包含 SKILL.md 的<b>文件夹</b>，或打包后的 <b>.zip</b> 文件
          </div>

          <ChoiceRow onClick={(e) => e.stopPropagation()}>
            <Button icon={<FileZipOutlined />} onClick={handleZipChoose}>
              选择 zip 文件
            </Button>
            <Button icon={<FolderOpenOutlined />} type="primary" ghost onClick={handleFolderChoose}>
              选择整个文件夹
            </Button>
            <Tooltip title="选完文件夹后，会自动读取里面的 SKILL.md 并填好下方表单">
              <Button type="text" icon={<InfoCircleOutlined />} />
            </Tooltip>
          </ChoiceRow>

          {/* 隐藏的两个 input */}
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            accept=".zip,.tar,.gz,.tgz"
            onChange={handleFileChange}
          />
          <input
            ref={folderInputRef}
            type="file"
            style={{ display: 'none' }}
            webkitdirectory=""
            directory=""
            multiple
            onChange={handleFileChange}
          />
        </DropZone>

        {/* ============ 文件列表 ============ */}
        {files.length > 0 && (
          <>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <Tag color={folderMode ? 'blue' : 'green'} icon={folderMode ? <FolderOpenOutlined /> : <FileZipOutlined />}>
                  {folderMode ? `文件夹模式 · ${files.length} 个文件` : '单文件模式'}
                </Tag>
                {packageUrl && <Tag color="success" icon={<CheckCircleOutlined />}>已上传</Tag>}
              </Space>
              <Space>
                <Button size="small" danger onClick={() => { setFiles([]); setPackageUrl(null); }}>
                  清空
                </Button>
                <Button
                  size="small"
                  type="primary"
                  loading={uploading}
                  disabled={!!packageUrl}
                  onClick={uploadPackage}
                >
                  {packageUrl ? '已上传' : '立即上传'}
                </Button>
              </Space>
            </div>

            {uploading && <Progress percent={uploadProgress} style={{ marginTop: 8 }} />}

            <FileList>
              {files.slice(0, 50).map((f, idx) => (
                <FileRow key={idx}>
                  <span className="name">
                    {(f.webkitRelativePath || f.name)}
                  </span>
                  <span className="size">{formatSize(f.size)}</span>
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeFile(idx)}
                    style={{ marginLeft: 8 }}
                  />
                </FileRow>
              ))}
              {files.length > 50 && (
                <FileRow><span className="name" style={{ color: '#8c8c8c' }}>...还有 {files.length - 50} 个文件</span></FileRow>
              )}
            </FileList>
          </>
        )}

        <Divider />

        {/* ============ 表单 ============ */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ visibility: 'public', version: '1.0.0' }}
        >
          <Form.Item
            label="技能名称（中文展示名）"
            name="name"
            rules={[{ required: true, message: '请输入技能名称' }]}
          >
            <Input placeholder="例如：音乐视频生成器" size="large" />
          </Form.Item>

          <Form.Item
            label="技能标识（英文 slug，唯一）"
            name="slug"
            rules={[
              { required: true, message: '请输入技能标识' },
              { pattern: /^[a-z0-9-_]+$/, message: '只允许小写字母、数字、连字符和下划线' },
            ]}
          >
            <Input placeholder="例如：music-video-creator" size="large" />
          </Form.Item>

          <Form.Item
            label="分类"
            name="category"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select placeholder="选择一个分类" size="large">
              {categories.map((cat) => (
                <Select.Option key={cat.value} value={cat.value}>
                  {cat.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <span>
                技能描述 
                <Tooltip title="自动从 SKILL.md 文件读取，可直接编辑">
                  <InfoCircleOutlined style={{ color: '#1890ff', marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            name="description"
            rules={[{ required: true, message: '请输入技能描述' }]}
          >
            <TextArea 
              rows={10} 
              placeholder="上传 SKILL.md 后将自动填充..."
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>

          <Form.Item label="版本号" name="version">
            <Input placeholder="例如：1.0.0" />
          </Form.Item>

          <Form.Item label="标签" name="tags">
            <Input placeholder="用逗号分隔，如：音乐, AI, 创作" />
          </Form.Item>

          <Form.Item label="可见性" name="visibility">
            <Radio.Group>
              <Radio value="public">公开</Radio>
              <Radio value="private">私有</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="技能图标（可选）">
            <IconUploader onClick={() => iconInputRef.current?.click()}>
              {iconFile ? (
                <img
                  src={URL.createObjectURL(iconFile)}
                  alt="icon"
                  style={{ width: 96, height: 96, objectFit: 'contain', borderRadius: 8 }}
                />
              ) : (
                <>
                  <FileMarkdownOutlined style={{ fontSize: 36, color: '#bfbfbf' }} />
                  <span style={{ color: '#8c8c8c', marginTop: 8, fontSize: 13 }}>点击上传图标</span>
                </>
              )}
              <input
                ref={iconInputRef}
                type="file"
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleIconSelect}
              />
            </IconUploader>
            {iconUrl && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#52c41a' }}>
                <CheckCircleOutlined /> 图标已上传
              </div>
            )}
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={submitting || uploading}
              disabled={files.length === 0}
            >
              {files.length === 0 ? '请先选择技能包' : (packageUrl ? '提交审核' : '上传并提交审核')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </PublishContainer>
  );
}

export default Publish;
